// CameraOfflineDialog.js
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Divider,
  Chip,
  Grid
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  NetworkCheck as NetworkIcon
} from '@mui/icons-material';

const CameraOfflineDialog = ({
  open,
  onClose,
  event
}) => {
  if (!event) return null;

  // Extract camera info from the event
  const cameraName = event.cameraName || event.name || 'Unknown Camera';
  const cameraIp = event.cameraIp || event.ip || 'Unknown IP';
  const area = event.area || event.attributes?.area || 'Unknown Area';
  const location = event.location || event.attributes?.location || cameraName;
  const lastUpdate = event.lastUpdate || event.timeStamp || 'Unknown';
  const currentStatus = event.newStatus || 'offline';

  // Format the timestamp for better display
  const formatTimestamp = (timestamp) => {
    if (!timestamp || timestamp === 'Unknown') return 'Unknown';
    
    try {
      // Handle different timestamp formats
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return timestamp; // Return original if can't parse
      }
      
      return date.toLocaleString('en-IN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return timestamp;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'online': return '#4caf50';
      case 'offline': return '#f44336';
      default: return '#ff9800';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1e2a38',
          color: 'white',
          borderRadius: '12px',
          border: '1px solid #3a4d63'
        }
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: '#263340',
          borderBottom: '1px solid #3a4d63',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pb: 2
        }}
      >
        <CameraIcon sx={{ color: '#f44336' }} />
        <Box>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Camera Status Alert
          </Typography>
          <Typography variant="caption" sx={{ color: '#637592' }}>
            Camera has gone offline
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={2}>
          {/* Camera Name Section */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CameraIcon sx={{ color: '#1ABC9C', mr: 1 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#637592', fontSize: '0.8rem' }}>
                  CAMERA NAME
                </Typography>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 500 }}>
                  {cameraName}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ bgcolor: '#3a4d63' }} />
          </Grid>

          {/* Camera IP Section */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <NetworkIcon sx={{ color: '#ff9800', mr: 1, fontSize: 20 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#637592', fontSize: '0.75rem' }}>
                  CAMERA IP
                </Typography>
                <Typography variant="body1" sx={{ color: 'white', fontFamily: 'monospace' }}>
                  {cameraIp}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Area Section */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocationIcon sx={{ color: '#2196f3', mr: 1, fontSize: 20 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#637592', fontSize: '0.75rem' }}>
                  AREA
                </Typography>
                <Typography variant="body1" sx={{ color: 'white' }}>
                  {area}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Location Section */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocationIcon sx={{ color: '#9c27b0', mr: 1, fontSize: 20 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#637592', fontSize: '0.75rem' }}>
                  LOCATION
                </Typography>
                <Typography variant="body1" sx={{ color: 'white' }}>
                  {location}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ bgcolor: '#3a4d63' }} />
          </Grid>

          {/* Current Status Section */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <NetworkIcon sx={{ color: '#ff5722', mr: 1 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#637592', fontSize: '0.75rem', mb: 1 }}>
                  CURRENT STATUS
                </Typography>
                <Chip
                  label={currentStatus.toUpperCase()}
                  size="small"
                  sx={{
                    bgcolor: getStatusColor(currentStatus),
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.7rem'
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Last Update Section */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TimeIcon sx={{ color: '#607d8b', mr: 1 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#637592', fontSize: '0.75rem' }}>
                  LAST UPDATE
                </Typography>
                <Typography variant="body1" sx={{ color: 'white', fontFamily: 'monospace' }}>
                  {formatTimestamp(lastUpdate)}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: '#263340',
          borderTop: '1px solid #3a4d63',
          px: 3,
          py: 2
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            bgcolor: '#1ABC9C',
            color: 'white',
            fontWeight: 'bold',
            '&:hover': {
              bgcolor: '#16a085'
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CameraOfflineDialog;